import { supabase } from "@/integrations/supabase/client";
import { ContractData, ContractSection, GeneratedContract } from "@/types/contract.types";

// MSA template content from the Docs folder
const MSA_TEMPLATE = `**Master Services Agreement**

This Master Services Agreement ("*MSA*") is dated {{CURRENT_DATE}} and is between Spherical LLC, a New York limited liability company ("*Consultant*"), and the undersigned client ("*Client*"). Capitalized terms not defined in context have the meanings given in Section 10\\.

1\\.          	*Services; Client Obligations*

1.1.      	*Services*. Consultant shall provide the services described in the applicable SOW ("Services"). Although Consultant may provide tangible items to Client, this Agreement is a contract for services and not the sale of goods.

1.2.      	*Obligations of Client*. In addition to any Client obligations specified in the applicable SOW, Client shall cooperate with Consultant in the delivery of Services by timely responding to requests for information or feedback, providing needed materials and information in a usable format, making key personnel reasonably available for consultation during the course of the project, and otherwise complying with its obligations under this Agreement.

2\\.          	*Fees & Expenses*

2.1.      	*Invoicing and Payment*. Consultant shall deliver invoices via e-mail to the Billing Contact provided in the SOW. Client shall pay Consultant all fees, reimbursable expenses, and any applicable sales, use or value added taxes, even if assessed after payment. Invoices are due upon receipt. If an invoice is unpaid 15 days after the date such invoice was delivered to Client, such invoice shall bear interest at 1.5% per month from the date delivered to Client until paid in full, and Consultant may suspend Services until all amounts due have been paid in full.

2.2.      	*Expenses*. Client shall reimburse Consultant for all out of pocket expenses incurred by Consultant as provided in the SOW or otherwise approved by Client.

3\\.          	*Intellectual Property*

3.1.      	*Ownership of Existing IP*. Each party will own and retain all rights to its pre-existing IP and any IP developed outside of the Deliverables.

3.2.      	*Assignment of Deliverables.* Upon payment in full of all amounts due under the applicable SOW, all rights in the Deliverables other than the Consultant Tools and the Third-Party IP, are hereby assigned to Client; Consultant will take such actions as are reasonably necessary to evidence and perfect Client's rights in the Deliverables.

3.3.      	*Consultant Tools.* If any Consultant Tools are incorporated into the Deliverables, subject to the condition of Client's payment in full of amounts due under the applicable SOW, Consultant hereby grants to Client a worldwide, nonexclusive, perpetual, royalty-free, license to use the Consultant Tools for internal purposes only in the manner contemplated by the SOW. Client shall not modify, create derivatives of, reverse engineer, decompile, or disassemble, or in any manner alter the Consultant Tools.

3.4.      	*Third-Party IP*. All Third-Party IP are the exclusive property of their respective owners, and subject to the terms of the applicable license.

*3.5.*      	*Use of Client Materials*

3.5.1.  	Client grants Consultant a nonexclusive, revocable license to access, copy, use and distribute any Client Materials provided to it to the extent necessary to perform the Services and for the purposes specified in Section 3.5.2 below. Client retains all other interest in Client Materials. Consultant may sublicense the right to use Client Materials to its subcontractors only to the extent necessary for subcontractors to perform services for Consultant related to this Agreement. Consultant may rely on and use the Client Materials without independent verification.

3.5.2.  	Consultant may (a) display Client Materials and Deliverables on its websites, blogs and social media pages, and (b) publicly describe its role in creation of the Deliverables and relationship with Client, for promotional, marketing and industry recognition purposes.

3.6.      	*Authorized Access*. Consultant is authorized to access Client's website, hosting provider, servers, development environment, and online services as may be necessary to provide the Deliverables. Consultant may use such access to limit or remove access to the Deliverables until paid for in full.

3.7.      	*Open Source*. In its work, Consultant may use libraries of, and submit back improvements to, "open source" software. Such open source libraries and improvements are subject to the terms of the open-source license governing the applicable software.

4\\.          	*Term and Termination*

*4.1.*      	*Termination by Client or Consultant.* Either Client or Consultant may terminate an SOW with 60 days' advance written notice without any penalty or termination fee. If either party terminates the agreement, Client will pay all fees and reimbursable expenses incurred by Consultant through the termination date, but no additional fees or penalties will apply.

5\\.          	*Warranties*

5.1.      	*By Consultant*. Consultant warrants to Client that:

5.1.1.  	Consultant has the right and authority to enter into this Agreement;

5.1.2.  	Consultant has and will maintain any approvals, licenses, filings, or registrations necessary to perform its obligations under this Agreement;

5.1.3.  	The Services will be performed in a professional manner in accordance with industry standards; and

5.1.4.  	To Consultant's knowledge, the Deliverables will not infringe upon or violate any copyright, trade secret, trademark, patent, invention, privacy, or non-disclosure rights of any third party.

**Except as expressly provided herein and to the extent permitted by applicable law, the Services and Deliverables are provided "AS IS." To the maximum extent permitted by law, Consultant disclaims any and all other warranties, whether express or implied, including, but not limited to, any implied warranties of merchantability, or fitness for a particular purpose, or non-infringement of intellectual property rights.**

5.2.      	*By Client*. Client warrants to Consultant that:

5.2.1.  	Client has the right and authority to enter into this Agreement;

5.2.2.  	Client shall at all times comply with all applicable laws, rules, statutes, regulations and other legal obligations to which it is subject in connection with its contemplated and actual performance under this Agreement;

5.2.3.  	Client's use of the Deliverables will comply with all applicable laws and restrictions governing the Deliverables; and

5.2.4.  	To Client's knowledge, the Client Materials do not infringe upon or violate any copyright, trade secret, trademark, patent, invention, privacy, or non-disclosure rights of any third party.

6\\.          	*Non-Solicitation of Employees*. Client will not, during the term of this Agreement or for one year thereafter, solicit or attempt to solicit, directly or indirectly, any of Consultant's employees. Should Client do so, and should the solicited employee resign or otherwise negatively alter his or her relationship with Consultant, Client will pay to Consultant a conversion fee equal to fifty percent of such employee's base salary during the twelve months preceding the resignation or other act negatively altering employment. Client agrees that damages for breach of this provision would be difficult to determine, but that the conversion fee is, nonetheless, a reasonable estimation of them.

7\\.          	*Indemnification*

7.1.      	*By Consultant*. Consultant shall indemnify Client, its Affiliates, and their respective directors, officers, employees, agents, successors, and assigns (each a "*Client Indemnified Party*") against all Claims to the extent that such Claims arise from Consultant's material breach of any warranty or covenant in this Agreement. Consultant has no liability under this Section 7.1 to the extent that Claims related to: (i) the negligent or willful acts of a Client Indemnified Party; (ii) Consultant's compliance with the instructions of Client, or (iii) a claim that a Deliverable is infringing where the alleged infringement is due to modifications made by Client or the inclusion of Client Materials in the Deliverable.

7.2.      	*By Client*. Client shall indemnify Consultant, its Affiliates, and each of their respective directors, officers, employees, agents, successors, and assigns (each a "*Consultant Indemnified Party*") against all Claims to the extent that the Claims arise from (i) Client's breach of any of the terms or provisions of this Agreement, including the confidentiality obligations set forth herein, or any SOW, (ii) Client's use of the Deliverables, (iii) actual or alleged infringement, misappropriation or violation of any U.S. trademark, copyright, patent, trade secret or other intellectual property or proprietary rights of a third party in connection with any materials provided by Client to Consultant for the purposes of providing the consulting services; and (iv) actual or alleged violation of any applicable U.S. law, rule or regulation in the performance of Client's obligations hereunder. 

7.3.      	*Indemnification Procedures*. The indemnified party will: (1) provide the indemnifying party with reasonably prompt notice of Claims; (2) permit the indemnifying party through mutually acceptable counsel to answer and defend Claims; and (3) provide the indemnifying party with reasonable information and assistance to help the indemnifying party defend Claims at the indemnifying party's expense. Any indemnified party may employ separate counsel and participate in the defense of any Claim at its own expense.

7.4.      	*Acknowledgment of Fault and Settling Claims*. Neither party will admit any fault or liability on the part of the other without prior written consent. The indemnifying party will not settle any Claim or publicize any settlement without the other party's prior written consent.

7.5.      	*Infringement Remedies*. In the event of a breach by Consultant of its warranties under Section 5.1.4, Consultant shall promptly replace the affected Deliverable with a complying equivalent or, if such equivalent is not reasonably available, Consultant shall refund to Client the Fees paid by Client for such Deliverable and Client shall discontinue use of such Deliverable. The remedy provided in this Section 7.5 is Client's sole remedy for Consultant's breach of the warranty given in Section 5.1.4.

8\\.          	*Limitation of Liability*

8.1.      	***No Consequential Damages*****. Except for damages arising from gross negligence or intentional wrongdoing, neither party shall be liable for any consequential, special, indirect, incidental, exemplary, or punitive damages (including damages for loss of data, revenue, or profits), whether or not foreseeable, regardless of the theory of such damages, and even if advised of the possibility of such damages.**

8.2.      	***Liability Cap*****. Consultant's total liability to Client arising out of or related to this Agreement, whether in contract or in tort, direct or by indemnity, will not exceed the fees paid by Client to Consultant for use of the Services at issue during the 3 months prior to the event giving rise to the liability.** 

9\\.          	*Confidentiality.* Each party acknowledges the other party's proprietary interest in and title to all confidential and proprietary information of such party, including information relating to their respective operations, employees, contractors, agents, customers, suppliers, unique ideas, techniques data, drawings, designs, procedures, trade secrets, know-how, passwords, source code, access code, and processes ("Proprietary Information").  Proprietary Information does not include any information independently developed or acquired by a party without obligation of confidentiality, or information that is in the public domain.  The parties hereby agree and confirm that they shall not claim any intellectual property rights over any of the Proprietary Information of the other party, or any other information derived therefrom.

During the Term of this Agreement and for five (5) years thereafter, and except as may be required by law, each party: (a) shall keep the other's Proprietary Information strictly confidential; (b) shall protect the other's Proprietary Information with the highest degree of care; (c) shall use the other's Proprietary Information solely for the purpose of this Agreement and the execution of the SOWs governed by this Agreement; and (d) shall not reproduce copy or duplicate the other's Proprietary Information, or knowingly allow any person to do so without prior written approval of the owner thereof.  Neither party shall disclose, transfer or otherwise make available the other's Proprietary Information to any third party.  Client and Consultant agree that the other's Proprietary Information may only be disclosed to those employees and contractors who have a need to know for the exclusive and single purpose of the execution of this Agreement and the SOWs that are governed by it.  Each party shall cause their respective officers, employees, and contractors to comply with this clause.

10\\.       	*Definitions*

10.1.   	"*Affiliate*" means any legal entity that owns, is owned by, or is commonly owned with a party. "Own" means having more than 50% ownership or the right to direct the management of the entity.

10.2.   	"*Agreement*" means this MSA, any SOWs signed pursuant to this MSA and all User Stories developed pursuant to an SOW. Each SOW is incorporated by reference into this MSA. In the event of a conflict between this Agreement and an SOW, the SOW controls as to that SOW only.

10.3.   	"*Claim*" means all third-party claims, actions, demands, proceedings, damages, costs, and liabilities of any kind.

10.4.   	"*Client Materials*" means all materials, information, specifications, images, copy, software, documentation, know how, concepts, Trademarks, IP and other content provided by Client for use in preparing, or incorporation in, the Deliverables.

10.5.   	"*Deliverables*" means the work product and related IP developed or provided by Consultant (or a subcontractor of Consultant) for Client under an SOW.

10.6.   	"*Intellectual Property*" or "*IP*" means all intellectual property rights throughout the world, whether existing under statute or at common law or equity, now or hereafter recognized, including: (1) copyrights, trade secrets, trademarks and service marks, patents, inventions, designs, logos and trade dress, "moral rights, " mask works, publicity rights, and privacy rights; and (2) any application or right to apply for any of the rights referred to in Section 10.6(1) and all renewals, extensions and restorations of the forgoing.

10.7.   	"*Consultant Tools*" means (i) Consultant's pre-existing or independently developed proprietary tools, processes or IP used in the Deliverables, (ii) Deliverables specifically identified in an SOW as Consultant Tools or that will remain Consultant Tools even if developed in connection with the Deliverables, and (iii) any modifications to or derivative works of the forgoing that Consultant creates as part of the Deliverables, to the extent such modifications or derivative works are not uniquely applicable to the Deliverables.

10.8.   	"*SOW*" means a written description of the services and Deliverables to be provided by Consultant to Client that has been signed by both Consultant and Client.

10.9.   	"*Termination Fee*" means, for fixed fee projects, an amount equal to 25% of the fees that would otherwise be due for the canceled portion of the work, and for non-fixed fee projects, Termination Fee means an amount equal to 25% of the fees that Consultant reasonably estimates that would have been required to complete the project.

10.10.	"*Third-Party IP*" means proprietary materials owned by a third-party that are included in the Deliverables. Third-Party IP includes, without limitation, stock photography, video, illustrations or textures; open source software and creative commons licensed content.

10.11.	"*Trademarks*" means the specific trademarks, service marks and logos identified and provided by Client under an SOW.

10.12.	"*Unpermitted Delay*" means a voluntary or involuntary suspension of work at Client's request or by Client unreasonably (more than thirty days in the aggregate) or continuously (for fifteen days or more) failing to timely provide feedback, content, or approvals.

11\\.       	*Miscellaneous*

11.1.   	*Independent Contractor*.  Consultant is and shall always remain an independent contractor of Client.  Nothing in this Agreement shall make Consultant and Client partners, joint venturers, or otherwise associated in or with the business of the other or constitute or be deemed to constitute an agency relationship between the parties.  Neither party shall be liable for any debts, accounts, obligations, or other liabilities of the other party, its agents, or employees.  Neither party is authorized to incur debts or other obligations of any kind on the part of or as agent for the other except as may specifically be authorized in writing.  This Agreement does not authorize Consultant to contract for, or act in the name of, Client, to hire personnel as employees of Client, or to otherwise act on behalf of, or hold itself out as an agent or representative of, Client beyond the role that is given to Consultant as an independent contractor Consultant as stated in this Agreement and the SOWs.  All SOW Staff and SOW Support Services are employees of or engaged by Consultant, as the case may be, and in no case shall they be deemed to be employees of Client.  Consultant may utilize contractors in providing the Deliverables. Consultant will provide the appropriate equipment, software and other materials necessary to perform its services at Consultant's expense.

11.2.   	*Default and Right To Cure.*  In the event of any default or material breach of any obligations by any party hereunder, another party shall give written notice to such party of the circumstances of the alleged default or material breach, and the party so notified shall have thirty (30) days within which to cure the same; provided, however, that with respect to a default by Client to timely pay Fees, Client shall have five (5) business days after receipt of such written notice within which to cure such default. If the noticed default or material breach is timely cured, then performance shall continue under this Agreement as if no default or material breach had occurred. If there is no timely cure, then the party giving such notice may pursue any and all legal remedies available for the default or breach, including termination in the event the default or breach is material.  The failure to assert any default or material breach shall not constitute the waiver of or acquiescence in any other default or material breach hereunder.   

11.3.   	*Assignment*. Neither party shall assign its rights or obligations under this Agreement without the prior written consent of the other party. Notwithstanding the foregoing, either party may assign this Agreement in connection with a merger, consolidation or sale, transfer of exchange of all or substantially all of its assets or stock, or the sale of the business to which this Agreement relates.

11.4.   	*Attorneys' Fees*. The prevailing party in any dispute with respect to this Agreement, including in tort, is entitled to recover reasonable attorneys' fees, costs and expenses incurred with respect to such dispute and in any appeal.

11.5.   	*Waiver*. A party waiving compliance with a provision in this Agreement may only do so by a signed writing. Unless otherwise specified in the writing, such a waiver only relates to the provision being waived and the particular instance giving rise to the waiver. Failure to enforce a provision of this Agreement does not constitute a waiver.

11.6.   	*Governing Law; Jurisdiction*. The laws of the state of New York shall govern all matters arising out of this Agreement, including tort claims. The parties irrevocably consent to the exclusive jurisdiction and venue in the courts for Kings County, New York.

11.7.   	*Notices*. Notices may be provided either by electronic mail, nationally-recognized courier service, personal delivery or US mail.  All notices, claims, certificates, requests, demands or other communications hereunder shall be given in writing and shall be delivered personally, or provided either by electronic mail, nationally-recognized courier service, personal delivery or US mail, and shall be deemed to have been duly given when so delivered with receipt confirmed, or one (1) business day after the date of deposit with such nationally recognized overnight courier. All such notices, claims, certificates, requests, demands or other communications shall be addressed to the respective parties at the addresses set forth below or to such other address as the person to whom notice is to be given may have furnished to the other party in writing in accordance with this Agreement.

If to Consultant, to:

Spherical LLC

200 Centre Street, Third Floor

New York, NY 10013

If to Client, to:

{{CLIENT_NAME}}


11.8.   	*Severability*. Should any provision of this Agreement be determined by a court of competent jurisdiction to be unenforceable, such provision shall be modified to the minimum extent necessary to render it enforceable, or, if incapable of such modification, shall be severed herefrom, and the remainder of this Agreement shall be enforced.

11.9.   	*Non-Restrictive Relationship*. Consultant may provide the same or similar services to other customers.

11.10.	*Force Majeure.* Neither party will be liable to the other for failure to perform its obligations hereunder if and to the extent that such failure to perform results from causes beyond its control, including and without limitation: strikes, lockouts, or other industrial disturbances; civil disturbances; fires; acts of God; acts of a public enemy; compliance with any regulations, order, or requirement of any governmental body or agency; or inability to obtain transportation or necessary materials in the open market.

11.11.	*Entire Agreement*. Amendments to this Agreement are only effective if in writing and signed by the parties. This Agreement and any SOW comprise the entire understanding of the parties with respect to the work described in such SOW and supersede all prior understandings, whether written or oral. This Agreement may be signed in separate counterparts. Faxed or scanned signatures are the same as original signatures. Only the parties to this Agreement are intended to benefit from its provisions. This Agreement is the product of negotiation between sophisticated parties, and shall not be construed in favor of or against either party.

\\[*signature page follows*\\]

Client and Consultant have signed this Master Services Agreement, and make it effective as of the date first written above.

| "Consultant" | "Client"   |
| :---- | :---- |
| **Spherical LLC**     By: 					                                                         Name: {{CONSULTANT_NAME}}                                  Title: {{CONSULTANT_TITLE}}                       | **{{CLIENT_NAME}}**      By: 					  Name: {{CLIENT_NAME}} 						 Title: {{CLIENT_TITLE}} 						 |
|  |  |
|  |  |
`;

// SOW template content from the Docs folder
const SOW_TEMPLATE = `**Statement of Work**

**SOW#: 1**  
**SOW Date: {{CURRENT_DATE}}**  
**SOW Term: {{TERM_LENGTH}}**

| Client: {{CLIENT_NAME}}
Contact: {{CLIENT_NAME}}
Contact Phone: {{BILLING_PHONE}}  
Contact Email: {{BILLING_EMAIL}}  
*(If different than above, …)*
Billing Contact: {{BILLING_CONTACT}}  
Billing Phone: {{BILLING_PHONE}}  
Billing Email: {{BILLING_EMAIL}}  | Consultant: Spherical LLC
Contact: {{CONSULTANT_NAME}}  
Contact Phone:  
Contact Email:  
*(If different than above, …)*
Billing Contact: Mary Savel
Billing Phone:  
Billing Email: accounting@spherical.co |
| :---- | :---- |

**MSA**

This SOW is subject to the Master Services Agreement between Client and Consultant dated {{CURRENT_DATE}} ("*MSA*)". The terms of the MSA are incorporated into this SOW by reference. All capitalized terms used but not otherwise defined herein shall have the meanings set forth in the Agreement. This SOW can be terminated by either party in accordance with the Agreement.

# PROJECT OVERVIEW

{{PROJECT_NAME}}

# SERVICES & DELIVERABLES

{{DELIVERABLES}}

**CLIENT ASSUMPTIONS**

Consultant's obligation to provide the Deliverables on the budget and schedule specified by Consultant is conditioned on Client's cooperation with the items below and any other conditions reasonably identified by Consultant.

{{CLIENT_ASSUMPTIONS}}

**FEES & EXPENSES**

**Fees**

* The total fee for this Project is $\{{CONTRACT_VALUE}} (the "Fee"), to be invoiced and paid as follows:  
  {{PAYMENT_SCHEDULE}}
* Additional consulting, marketing and development services outside of the above scope may be purchased at our standard rate of $175/hr, based on availability.

**EXPENSES**

Client will reimburse Consultant for out-of-pocket expenses incurred with Client's prior written consent. 

**INVOICING**

Invoices for Fees and expenses will be delivered by email to the Client billing contact identified above.

**The Fee and schedule information in this SOW are based on the complexity of the Project as understood prior to commencing Services, the information Client has provided, and other factors. Because the details, functionality, specifications, and priorities of the Project may evolve as the Project progresses, it is possible that a change order may be necessitated. If that is the case, Consultant will notify Client, explain the circumstances, and the parties will negotiate in good faith regarding the preparation and execution of a change order.**

**EXECUTION**

The parties have executed this SOW and make it effective as of the SOW Date above.

| "Consultant" | "Client"  |
| :---- | :---- |
| **Spherical LLC**  
By: 					  
Name: {{CONSULTANT_NAME}}		 
Title: {{CONSULTANT_TITLE}}		 | **{{CLIENT_NAME}}**  
By: 					  
Name: {{CLIENT_NAME}}                 	 
Title: {{CLIENT_TITLE}}                                         	 |
`;

// Standard client assumptions
const DEFAULT_CLIENT_ASSUMPTIONS = [
  "* All Client Materials are approved, final and delivered in the designated format.",
  "* Consultant has the right to use all Client Materials.",
  "* Client has performed as required under this Agreement.",
  "* Client will provide Consultant with timely access to all Client information and personnel reasonably necessary for Consultant to perform its obligations under this Agreement.",
  "* Client will designate a single point of contact for all approvals.",
  "* Client will review, evaluate, and test (if applicable) all Deliverables, and provide any feedback to Consultant within 7 days of delivery.",
  "* All comments and feedback will be provided to Consultant in a comprehensive manner from the designated contact in a timely manner.",
  "* Client will be available for, and provide timely feedback at, regular status meetings.",
  "* Client will promptly license any necessary Third Party Materials."
];

export async function generateContract(
  organizationId: string,
  contractData: ContractData,
  msaSections: string[],
  sowSections: string[]
) {
  const msaContent = await generateMSAContent(contractData);
  const sowContent = await generateSOWContent(contractData);
  
  const { data: msa, error: msaError } = await supabase
    .from('generated_contracts')
    .insert({
      organization_id: organizationId,
      type: 'msa',
      content: msaContent,
      metadata: { 
        ...contractData,
        selected_sections: msaSections 
      },
      status: 'draft'
    })
    .select()
    .single();

  if (msaError) throw msaError;

  const { data: sow, error: sowError } = await supabase
    .from('generated_contracts')
    .insert({
      organization_id: organizationId,
      type: 'sow',
      content: sowContent,
      metadata: { 
        ...contractData,
        selected_sections: sowSections 
      },
      status: 'draft'
    })
    .select()
    .single();

  if (sowError) throw sowError;

  return { msa, sow };
}

async function generateMSAContent(data: ContractData) {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Replace placeholders in the MSA template
  const msaContent = MSA_TEMPLATE
    .replace(/{{CURRENT_DATE}}/g, currentDate)
    .replace(/{{CLIENT_NAME}}/g, data.organization_name)
    .replace(/{{CLIENT_TITLE}}/g, data.client_title || '')
    .replace(/{{CONSULTANT_NAME}}/g, data.consultant_name || 'Pablo Henderson')
    .replace(/{{CONSULTANT_TITLE}}/g, data.consultant_title || 'CMO & President');

  return msaContent;
}

async function generateSOWContent(data: ContractData) {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Default payment schedule if not provided
  const paymentSchedule = data.payment_schedule || `* $${data.contract_value} due upon signing`;
  
  // Default term length if not provided
  const termLength = data.term_length || "3 month";
  
  // Create client assumptions string from array or use defaults
  const clientAssumptions = (data.client_assumptions && data.client_assumptions.length > 0) 
    ? data.client_assumptions.join('\n')
    : DEFAULT_CLIENT_ASSUMPTIONS.join('\n');

  // Default deliverables if not provided
  const deliverables = data.deliverables || "## Services\n* Strategy development\n* Implementation\n* Reporting";

  // Create project name if not provided
  const projectName = data.project_name || `Services for ${data.organization_name}`;

  // Replace placeholders in the SOW template
  const contractValue = data.contract_value ? data.contract_value.toString() : '0';
  
  const sowContent = SOW_TEMPLATE
    .replace(/{{CURRENT_DATE}}/g, currentDate)
    .replace(/{{CLIENT_NAME}}/g, data.organization_name)
    .replace(/{{CLIENT_TITLE}}/g, data.client_title || '')
    .replace(/{{BILLING_CONTACT}}/g, data.billing_contact)
    .replace(/{{BILLING_EMAIL}}/g, data.billing_email)
    .replace(/{{BILLING_PHONE}}/g, data.billing_phone)
    .replace(/{{CONTRACT_VALUE}}/g, contractValue)
    .replace(/{{TERM_LENGTH}}/g, termLength)
    .replace(/{{PROJECT_NAME}}/g, projectName)
    .replace(/{{PAYMENT_SCHEDULE}}/g, paymentSchedule)
    .replace(/{{DELIVERABLES}}/g, deliverables)
    .replace(/{{CLIENT_ASSUMPTIONS}}/g, clientAssumptions)
    .replace(/{{CONSULTANT_NAME}}/g, data.consultant_name || 'Pablo Henderson')
    .replace(/{{CONSULTANT_TITLE}}/g, data.consultant_title || 'President & CMO');

  return sowContent;
}

export async function getOrganizationContracts(organizationId: string) {
  const { data, error } = await supabase
    .from('generated_contracts')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function generateContractPDF(contractId: string): Promise<string> {
  // This is a placeholder for PDF generation functionality
  // In a real implementation, you would:
  // 1. Fetch the contract from Supabase
  // 2. Use a PDF generation library/API to convert the markdown to PDF
  // 3. Store the PDF in storage and update the contract record with the file URL
  
  const { data: contract, error } = await supabase
    .from('generated_contracts')
    .select('*')
    .eq('id', contractId)
    .single();
    
  if (error) throw error;
  
  // Simulate generating a PDF and storing it
  const fakeFileUrl = `https://storage.example.com/contracts/${contractId}.pdf`;
  
  // Update the contract record with the file URL
  const { error: updateError } = await supabase
    .from('generated_contracts')
    .update({ file_url: fakeFileUrl })
    .eq('id', contractId);
    
  if (updateError) throw updateError;
  
  return fakeFileUrl;
}

export async function getContractTemplates() {
  const { data, error } = await supabase
    .from('contract_templates')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function saveContractTemplate(name: string, type: 'msa' | 'sow', sections: string[]) {
  const { data, error } = await supabase
    .from('contract_templates')
    .insert({
      name,
      type,
      sections,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}